/**
 * 출입통제선.
 *
 * **현장은 한서진의 집 하나다.** 통제선은 거기에만 친다.
 *
 * 처음에는 아직 순서가 아닌 구역의 문마다 통제선을 쳤는데, 그건 틀렸다.
 * 송이경은 살아 있는 사람이고 그 집은 사건 현장이 아니다. 산 사람의 집 문에
 * 폴리스 라인을 칠 이유가 없다.
 *
 * 그리고 통제선은 **막는 물건이 아니라 표시하는 물건**이다. 정우빈이 혼자
 * 지키는 현장이 어디까지인지를 보여줄 뿐, 문을 잠그지는 않는다.
 * (크라임씬은 애초에 공간을 막지 않는다 — research/20_크라임씬_게임구조.md)
 */

import * as THREE from 'three';
import { type PropDef } from '../../shared/level';
import { ep } from './episodeData';

/** 노랑·검정 사선. 폴리스 라인은 이 무늬 하나로 읽힌다 */
function tapeTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 64;
  const g = c.getContext('2d')!;
  g.fillStyle = '#d8b524';
  g.fillRect(0, 0, c.width, c.height);
  g.fillStyle = '#1a1a1a';
  for (let x = -64; x < c.width + 64; x += 64) {
    g.beginPath();
    g.moveTo(x, c.height);
    g.lineTo(x + 32, c.height);
    g.lineTo(x + 64, 0);
    g.lineTo(x + 32, 0);
    g.closePath();
    g.fill();
  }
  g.fillStyle = '#efe6cf';
  g.font = 'bold 30px "Noto Sans KR", system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText('출입통제 · 정선지구대', c.width / 2, c.height / 2 + 1);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

export interface Tapes {
  group: THREE.Group;
}

const SPAN = 1.9; // 문 개구부 폭
/** 사건 현장 — 시신이 나온 집 */
const SCENE = 'hanroom';

export function buildTapes(scene: THREE.Scene): Tapes {
  const group = new THREE.Group();
  scene.add(group);

  const mat = new THREE.MeshStandardMaterial({
    map: tapeTexture(),
    roughness: 0.75,
    side: THREE.DoubleSide,
    toneMapped: true,
  });


  for (const d of ep().PROPS) {
    if (d.kind !== 'door') continue;
    if (d.room !== SCENE) continue;      // 현장에만 친다
    if (d.id === 'closet') continue;     // 붙박이장에 통제선을 치지는 않는다

    const g = new THREE.Group();
    g.position.set(d.pos[0], 0, d.pos[2]);
    // rot 0 / PI 은 가로벽(문이 ±Z 를 본다), ±PI/2 은 세로벽
    g.rotation.y = -(d.rot ?? 0);

    // 두 줄을 엇갈리게 친다. 한 줄이면 그냥 띠지만, 엇갈리면 「막혔다」가 된다
    for (const [y, tilt] of [
      [0.95, 0.16],
      [1.55, -0.13],
    ] as const) {
      // 판이 아니라 얇은 상자다. 문짝이 문틀보다 5cm 앞에 서 있어서, 문 평면에
      // 판을 두면 문짝 뒤로 숨는다. 양쪽으로 삐져나오게 두께를 준다
      const strip = new THREE.Mesh(new THREE.BoxGeometry(SPAN * 1.02, 0.1, 0.26), mat);
      strip.position.set(0, y, 0);
      strip.rotation.z = tilt;
      g.add(strip);
    }
    group.add(g);
  }

  // 현장 통제선은 걷히지 않는다. 라운드와 무관하게 계속 서 있다
  return { group };
}

/** 이 문이 통제선 안쪽인가 (프롬프트 문구를 고를 때 쓴다) */
export function isDoor(def: PropDef): boolean {
  return def.kind === 'door' && def.id !== 'closet';
}
